import type { ReactElement } from 'react';
import React, { useEffect, useState } from 'react';

import { Box, Grid, ThemeConstellation } from '@zillow/constellation';

import type { getOmpDataQuery } from '../../__generated__/graphql';
import { CONTAINER_SX } from '../../constants/styles';
import { useGlobalStoreState } from '../../contexts/GlobalStoreProvider';
import { getRTBCards } from './constants';
import RTBCardLoaded from './RTBCardLoaded';
import RTBCardLoading from './RTBCardLoading';

const RTBCardList: React.FC<RTBCardListProps> = ({
    loading = false,
    onsiteMessageData = {},
}): ReactElement => {
    const { isVisualRefreshEnabled } = useGlobalStoreState();

    const rtbCards = getRTBCards(isVisualRefreshEnabled);

    const isOmpDataComplete = rtbCards.every((card) =>
        onsiteMessageData.onsiteMessage?.messages?.some(
            (entry) => card.placementName === entry?.placementName && !!entry.selectedTreatment
        )
    );

    const [popUpNo, setPopUpNo] = useState(-1);
    useEffect(() => {
        if (loading) {
            return;
        }

        let onAppear: number;
        onAppear = rtbCards.length;
        const showUp = setInterval(() => {
            if (onAppear > 0) {
                setPopUpNo(rtbCards.length - onAppear);
                onAppear -= 1;
            } else {
                setPopUpNo(rtbCards.length);
                clearInterval(showUp);
            }
        }, 220);
    }, [loading]);

    return (
        <Box
            sx={{
                backgroundColor: '$colors.backgroundLight',
                width: '100%',
            }}
        >
            <Box
                as={Grid}
                sx={{
                    ...CONTAINER_SX,
                    ...(isVisualRefreshEnabled
                        ? {
                              // eslint-disable-next-line max-len
                              // eslint-disable-next-line zillow/@typescript-eslint/naming-convention
                              '@media $lg_lte': {
                                  gridTemplateColumns: 'repeat(1, 1fr) !important',
                              },
                          }
                        : {
                              'width': '1200px',
                              'maxWidth': '100%',
                              'padding': '$spacing.xl $spacing.none',
                              // eslint-disable-next-line max-len
                              // eslint-disable-next-line zillow/@typescript-eslint/naming-convention
                              '@media $lg_gte': {
                                  padding: '$spacing.xl $spacing.sm',
                              },
                              // eslint-disable-next-line max-len
                              // eslint-disable-next-line zillow/@typescript-eslint/naming-convention
                              '@media $lg_lte': {
                                  width: '700px',
                              },
                              // eslint-disable-next-line max-len
                              // Between 481 and 596px width, the card content should be vertically presented
                              // This is a special treatment as per rtb guidelines figma: https://www.figma.com/file/uvBgJmVpXfQcwq4fjN846v/OMP-Guidelines?type=design&node-id=70%3A15249&mode=design&t=eWStg2NLdLv6Apz3-1
                              [`@media screen and (min-width: ${ThemeConstellation.constellation.breakpoints.md}px) and
              (max-width: 596px)`]: {
                                  // Each individual card
                                  '> *': {
                                      marginLeft: '$spacing.xl',
                                      marginRight: '$spacing.xl',
                                  },
                              },
                              [`@media screen and (min-width: 597px) and
                  (max-width: ${ThemeConstellation.constellation.breakpoints.xl}px)`]: {
                                  // Each individual card
                                  '> *': {
                                      marginLeft: '$spacing.md',
                                      marginRight: '$spacing.md',
                                  },
                              },
                              // At 1024px width, the cards are stacked vertically.
                              // This is a special treatment as per rtb guidelines figma: https://www.figma.com/file/uvBgJmVpXfQcwq4fjN846v/OMP-Guidelines?type=design&node-id=0-1&mode=design&t=fctue7PnpRoMF0Dw-0
                              [`@media screen and (max-width: ${ThemeConstellation.constellation.breakpoints.xl}px)`]:
                                  {
                                      gridTemplateColumns: 'repeat(1, 1fr) !important',
                                  },
                          }),
                    '[class^="StyledCard-"]': {
                        ...(isVisualRefreshEnabled
                            ? {}
                            : {
                                  border: 'none',
                              }),
                    },
                }}
                data-testid="hops-rtb-cards"
                display="grid"
                gridTemplateColumns={{
                    default: 'repeat(3, 1fr)',
                }}
                gap="md"
            >
                {rtbCards.map((card, index) => (
                    <React.Fragment key={card.placementName}>
                        {loading ? (
                            <RTBCardLoading />
                        ) : (
                            <RTBCardLoaded
                                card={card}
                                onsiteMessageData={onsiteMessageData}
                                additionalProps={{
                                    active: index === popUpNo,
                                    show: index <= popUpNo,
                                    zoom: { animationDuration: 200 },
                                }}
                                showFallback={!isOmpDataComplete}
                                index={index + 1}
                            />
                        )}
                    </React.Fragment>
                ))}
            </Box>
        </Box>
    );
};

interface RTBCardListProps {
    loading: boolean;
    onsiteMessageData: getOmpDataQuery | undefined;
}

export default RTBCardList;
